import { BookingRecord } from '../types/booking';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const bookingDetailQueryAtom = createQueryAtomFactory(
  'trade.bookingDetail',
  (params: { id: string }) => ({
    bookings: {
      __args: {
        filter: {
          ids: [params.id],
        },
        pagination: {
          limit: 1,
          offset: 0,
        },
      },
      entries: {
        id: true,
        facilityId: true,
        documentNumber: true,
        status: true,
        cutOffDate: true,
        earlyReturnDate: true,
        vesselArrivalDate: true,
        vesselDepartureDate: true,
        vesselName: true,
        loadingPortId: true,
        loadingPort: {
          id: true,
          unLocode: true,
          name: true,
          countryCode: true,
        },
        pickupTerminalId: true,
        pickupTerminal: {
          id: true,
          terminalCode: true,
          name: true,
        },
        destinationPortId: true,
        destinationPort: {
          id: true,
          unLocode: true,
          name: true,
          countryCode: true,
        },
        dropoffTerminalId: true,
        dropoffTerminal: {
          id: true,
          terminalCode: true,
          name: true,
        },
        steamshipLine: true,
        oceanFreight: true,
        internalNotes: true,
        externalNotes: true,
        containers: {
          containerType: {
            id: true,
            name: true,
          },
          quantity: true,
        },
        photoRequirements: {
          description: true,
          required: true,
        },
        files: {
          id: true,
          fileName: true,
          fileSize: true,
          mimeType: true,
          status: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
        salesOrders: {
          id: true,
          documentNumber: true,
          counterpartyReferenceNumber: true,
        },
        loadStatusSummary: {
          total: true,
          shipped: true,
          received: true,
          reconciled: true,
          allShipped: true,
          allReceived: true,
          allReconciled: true,
        },
      },
    },
  }),
  {
    select: (
      data,
    ): {
      booking: BookingRecord | null;
    } => {
      if (!data?.bookings?.entries?.length) {
        return { booking: null };
      }

      const booking = data.bookings.entries[0];

      return {
        booking: {
          id: booking.id,
          facilityId: booking.facilityId,
          bookingNumber: booking.documentNumber,
          status: booking.status,
          cutOffDate: maybeParseCalendarDate(booking.cutOffDate),
          earlyReturnDate: maybeParseCalendarDate(booking.earlyReturnDate),
          vesselArrivalDate: maybeParseCalendarDate(booking.vesselArrivalDate),
          vesselDepartureDate: maybeParseCalendarDate(
            booking.vesselDepartureDate,
          ),
          vesselName: booking.vesselName ?? undefined,
          loadingPortId: booking.loadingPortId ?? undefined,
          loadingPort: booking.loadingPort ?? undefined,
          pickupTerminalId: booking.pickupTerminalId ?? undefined,
          pickupTerminal: booking.pickupTerminal ?? undefined,
          destinationPortId: booking.destinationPortId ?? undefined,
          destinationPort: booking.destinationPort ?? undefined,
          dropoffTerminalId: booking.dropoffTerminalId ?? undefined,
          dropoffTerminal: booking.dropoffTerminal ?? undefined,
          steamshipLine: booking.steamshipLine ?? undefined,
          oceanFreight: booking.oceanFreight ?? undefined,
          internalNotes: booking.internalNotes ?? undefined,
          externalNotes: booking.externalNotes ?? undefined,
          containers:
            booking.containers?.map((container) => ({
              containerType: {
                id: container.containerType.id,
                name: container.containerType.name,
                timestamps: {
                  createdAt: '',
                  updatedAt: '',
                },
              },
              quantity: container.quantity,
            })) ?? [],
          photoRequirements:
            booking.photoRequirements?.map((req) => ({
              description: req.description,
              required: req.required,
            })) ?? [],
          files:
            booking.files?.map((file) => ({
              id: file.id,
              fileName: file.fileName,
              fileSize: file.fileSize,
              mimeType: file.mimeType,
              status: file.status,
              downloadUrl: undefined, // Will be generated on-demand via useFileDownload
            })) ?? [],
          timestamps: {
            createdAt: parseZonedDateTime(booking.timestamps.createdAt),
            updatedAt: parseZonedDateTime(booking.timestamps.updatedAt),
          },
          salesOrders: booking.salesOrders.map((salesOrder) => ({
            id: salesOrder.id,
            documentNumber: salesOrder.documentNumber,
            counterpartyReferenceNumber:
              salesOrder.counterpartyReferenceNumber ?? undefined,
          })),
          loadStatusSummary: booking.loadStatusSummary
            ? {
                total: booking.loadStatusSummary.total,
                shipped: booking.loadStatusSummary.shipped,
                received: booking.loadStatusSummary.received,
                reconciled: booking.loadStatusSummary.reconciled,
                allShipped: booking.loadStatusSummary.allShipped,
                allReceived: booking.loadStatusSummary.allReceived,
                allReconciled: booking.loadStatusSummary.allReconciled,
              }
            : undefined,
        },
      };
    },
  },
);
